import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, Sparkles } from "lucide-react";
import api from "../../lib/api";
import { errText } from "../../lib/errors";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import MediaPicker from "../../components/MediaPicker";

const EMPTY = { name: "", image: "", description: "", order: 0, seo_title: "", seo_description: "", seo_keywords: "", landing_enabled: false, landing_hero: "", landing_content: "", landing_faq: [] };

export default function AdminCategories() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [seoBusy, setSeoBusy] = useState(false);

  const suggestSeo = async () => {
    if (!form.name) { toast.error("Önce kategori adı girin"); return; }
    setSeoBusy(true);
    try {
      const { data } = await api.post("/admin/seo/suggest", { title: form.name, content: form.description || form.name, kind: "category" });
      setForm((f) => ({ ...f, seo_title: data.seo_title || f.seo_title, seo_description: data.seo_description || f.seo_description }));
      toast.success(`SEO önerisi uygulandı${data.keywords?.length ? ` — anahtar kelimeler: ${data.keywords.slice(0, 4).join(", ")}` : ""}`);
    } catch { toast.error("SEO önerisi alınamadı"); }
    finally { setSeoBusy(false); }
  };

  const load = () => api.get("/admin/categories").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const [landBusy, setLandBusy] = useState(false);
  const [editSlug, setEditSlug] = useState("");

  const suggestLanding = async () => {
    if (!form.name) { toast.error("Önce kategori adı girin"); return; }
    setLandBusy(true);
    try {
      const { data } = await api.post("/admin/categories/ai-landing", { name: form.name, description: form.description || form.name });
      setForm((f) => ({
        ...f,
        landing_hero: data.landing_hero || f.landing_hero,
        landing_content: data.landing_content || f.landing_content,
        landing_faq: Array.isArray(data.landing_faq) && data.landing_faq.length ? data.landing_faq : f.landing_faq,
        seo_title: data.seo_title || f.seo_title,
        seo_description: data.seo_description || f.seo_description,
        seo_keywords: data.seo_keywords || f.seo_keywords,
      }));
      toast.success("AI landing içeriği uygulandı");
    } catch { toast.error("AI içerik önerisi alınamadı"); }
    finally { setLandBusy(false); }
  };

  const setFaq = (i, k, v) => setForm((f) => { const faq = [...(f.landing_faq || [])]; faq[i] = { ...faq[i], [k]: v }; return { ...f, landing_faq: faq }; });

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/admin/categories/${editId}`, form);
      else await api.post("/admin/categories", form);
      toast.success("Kategori kaydedildi");
      setOpen(false); setForm(EMPTY); setEditId(null);
      load();
    } catch (err) { toast.error(errText(err) || "Kaydedilemedi"); }
  };

  const del = async (c) => {
    if (!window.confirm(`"${c.name}" silinsin mi?`)) return;
    try { await api.delete(`/admin/categories/${c.category_id}`); toast.success("Silindi"); load(); }
    catch (err) { toast.error(errText(err) || "Silinemedi"); }
  };

  const inputCls = "w-full h-11 px-4 rounded-xl border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-forest/30";

  return (
    <div data-testid="admin-categories-page">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-2xl sm:text-3xl text-forest">Kategoriler</h1>
        <button data-testid="add-category-button" onClick={() => { setForm(EMPTY); setEditId(null); setEditSlug(""); setOpen(true); }} className="flex items-center gap-2 bg-forest hover:bg-forest-dark text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors"><Plus className="w-4 h-4" /> Yeni Kategori</button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        {items.map((c) => (
          <div key={c.category_id} data-testid={`category-row-${c.slug}`} className="bg-white rounded-2xl border border-stone-200/80 p-4 flex items-center gap-4 shadow-sm">
            <img src={c.image} alt="" className="w-14 h-14 rounded-xl object-cover bg-cream-dark" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-stone-900">{c.name}</p>
              <p className="text-xs text-stone-400 font-mono">/{c.slug} · sıra {c.order} {c.landing_enabled && <span className="not-italic font-sans text-[10px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded ml-1">LANDING</span>}</p>
            </div>
            <button data-testid={`edit-category-${c.slug}`} onClick={() => { setForm({ name: c.name, image: c.image, description: c.description, order: c.order, seo_title: c.seo_title || "", seo_description: c.seo_description || "", seo_keywords: c.seo_keywords || "", landing_enabled: !!c.landing_enabled, landing_hero: c.landing_hero || "", landing_content: c.landing_content || "", landing_faq: c.landing_faq || [] }); setEditId(c.category_id); setEditSlug(c.slug); setOpen(true); }} className="p-2 rounded-lg hover:bg-cream-dark text-stone-600"><Pencil className="w-4 h-4" /></button>
            <button data-testid={`delete-category-${c.slug}`} onClick={() => del(c)} className="p-2 rounded-lg hover:bg-red-50 text-terra"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Kategori Düzenle" : "Yeni Kategori"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4 mt-2">
            <input data-testid="category-name-input" required className={inputCls} placeholder="Kategori adı *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <div className="flex gap-2">
              <input className={`${inputCls} flex-1`} placeholder="Görsel URL" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} />
              <MediaPicker type="image" testid="category-media-picker" onSelect={(url) => setForm({ ...form, image: url })} />
            </div>
            {form.image && (
              <div data-testid="category-image-preview" className="flex items-center gap-3 bg-stone-50 border border-stone-200 rounded-xl p-3">
                <img src={form.image} alt="Seçili görsel" className="w-16 h-16 rounded-xl object-cover ring-2 ring-gold/50" />
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-stone-700">Seçili görsel</p>
                  <p className="text-[11px] text-stone-400 truncate">{form.image}</p>
                </div>
                <button type="button" data-testid="category-image-clear" onClick={() => setForm({ ...form, image: "" })} className="ml-auto text-xs text-stone-400 hover:text-red-500">Kaldır</button>
              </div>
            )}
            <input className={inputCls} placeholder="Açıklama" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <input type="number" className={inputCls} placeholder="Sıralama" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} />
            <button type="button" data-testid="category-seo-suggest" onClick={suggestSeo} disabled={seoBusy} className="flex items-center justify-center gap-2 w-full border border-gold text-gold-dark hover:bg-gold/10 disabled:opacity-50 text-sm font-semibold py-2.5 rounded-xl transition-colors">
              <Sparkles className="w-4 h-4" /> {seoBusy ? "AI öneri hazırlanıyor..." : "AI SEO Önerisi Al"}
            </button>
            <input className={inputCls} placeholder="SEO Başlığı" value={form.seo_title} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} />
            <input className={inputCls} placeholder="SEO Açıklaması" value={form.seo_description} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} />
            <div className="border border-stone-200 rounded-xl p-3.5 space-y-3 bg-stone-50/60">
              <div className="flex items-center justify-between gap-2">
                <label className="flex items-center gap-2 text-sm font-semibold text-stone-800">
                  <input data-testid="category-landing-enabled" type="checkbox" checked={!!form.landing_enabled} onChange={(e) => setForm({ ...form, landing_enabled: e.target.checked })} className="w-4 h-4 accent-forest" />
                  SEO Landing Sayfası
                </label>
                {editId && editSlug && <a data-testid="category-landing-preview" href={`/kategori/${editSlug}`} target="_blank" rel="noreferrer" className="text-xs font-semibold text-forest hover:underline">Önizle →</a>}
              </div>
              {form.landing_enabled && (
                <>
                  <button type="button" data-testid="category-ai-landing" onClick={suggestLanding} disabled={landBusy} className="flex items-center justify-center gap-2 w-full border border-gold text-gold-dark hover:bg-gold/10 disabled:opacity-50 text-sm font-semibold py-2.5 rounded-xl transition-colors">
                    <Sparkles className="w-4 h-4" /> {landBusy ? "AI içerik hazırlanıyor..." : "AI ile Landing İçeriği Öner"}
                  </button>
                  <input data-testid="category-landing-hero" className={inputCls} placeholder="Hero başlığı (örn: Doğal Ballar — Katkısız & Yöresel)" value={form.landing_hero} onChange={(e) => setForm({ ...form, landing_hero: e.target.value })} />
                  <textarea data-testid="category-landing-content" rows={6} className="w-full px-4 py-3 rounded-xl border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-forest/30" placeholder={"Landing içeriği (markdown: ## başlık, **kalın**, - liste)"} value={form.landing_content} onChange={(e) => setForm({ ...form, landing_content: e.target.value })} />
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-stone-600">SSS (Sık Sorulan Sorular)</p>
                    {(form.landing_faq || []).map((f, i) => (
                      <div key={i} data-testid={`category-faq-row-${i}`} className="flex gap-2 items-start">
                        <div className="flex-1 space-y-1.5">
                          <input className={inputCls} placeholder="Soru" value={f.q} onChange={(e) => setFaq(i, "q", e.target.value)} />
                          <input className={inputCls} placeholder="Cevap" value={f.a} onChange={(e) => setFaq(i, "a", e.target.value)} />
                        </div>
                        <button type="button" data-testid={`category-faq-remove-${i}`} onClick={() => setForm({ ...form, landing_faq: form.landing_faq.filter((_, j) => j !== i) })} className="p-2 rounded-lg hover:bg-red-50 text-terra mt-1"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    ))}
                    <button type="button" data-testid="category-faq-add" onClick={() => setForm({ ...form, landing_faq: [...(form.landing_faq || []), { q: "", a: "" }] })} className="text-xs font-semibold text-forest hover:underline">+ Soru Ekle</button>
                  </div>
                  <input data-testid="category-seo-keywords" className={inputCls} placeholder="Anahtar kelimeler (virgülle)" value={form.seo_keywords} onChange={(e) => setForm({ ...form, seo_keywords: e.target.value })} />
                </>
              )}
            </div>
            <button data-testid="category-save-button" className="w-full bg-forest hover:bg-forest-dark text-white font-semibold py-3 rounded-xl transition-colors">Kaydet</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
